import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import './app-backfills.scss';

interface BackfillApp {
  id: string;
  name: string;
  jobName: string;
  supportsRefresh?: boolean;
}

interface UserResult {
  id: string;
  name: string | null;
}

interface JobStatusProgress {
  completed: number;
  total: number;
  created?: number;
  skipped?: number;
  failed?: number;
  refreshed?: number;
  notInstalled?: number;
  customised?: number;
}

interface JobStatus {
  id: string;
  name: string;
  state: string;
  progress: number | JobStatusProgress;
  data: { userIds: string[]; dryRun?: boolean; refreshViews?: boolean };
  returnvalue: any;
  failedReason: string | null;
}

const AppBackfills: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [apps, setApps] = useState<BackfillApp[]>([]);
  const [selectedAppId, setSelectedAppId] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [searchResults, setSearchResults] = useState<UserResult[]>([]);
  const [batch, setBatch] = useState<UserResult[]>([]);
  const [dryRun, setDryRun] = useState<boolean>(false);
  const [refreshViews, setRefreshViews] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<JobStatus | null>(null);

  const searchTimer = useRef<NodeJS.Timeout | null>(null);

  const withToken = useCallback(
    async <T,>(method: (token: string) => Promise<T>): Promise<T> => {
      const token = await getAccessTokenSilently();
      return method(token);
    },
    [getAccessTokenSilently],
  );

  useEffect(() => {
    withToken(async (token) => {
      try {
        const res = await api.get<{ success: boolean; apps: BackfillApp[] }>(
          '/app-backfills/apps',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setApps(res.data.apps);
        if (res.data.apps.length > 0) {
          setSelectedAppId(res.data.apps[0].id);
        }
        setHasAccess(true);
      } catch (err: any) {
        if (err?.response?.status === 403) {
          setHasAccess(false);
        } else {
          setError('Failed to load apps');
        }
      }
    });
  }, [withToken]);

  const fetchSearch = useCallback(
    async (query: string, limit = 20) => {
      if (!selectedAppId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await withToken(async (token) =>
          api.get<{ success: boolean; users: UserResult[] }>(
            '/app-backfills/users',
            {
              params: { search: query, limit },
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
        );
        setSearchResults(res.data.users || []);
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Failed to search users');
      } finally {
        setLoading(false);
      }
    },
    [selectedAppId, withToken],
  );

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchSearch(search);
    }, 250);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [search, fetchSearch]);

  useEffect(() => {
    if (!jobId) return;
    const interval = setInterval(() => {
      withToken(async (token) => {
        try {
          const res = await api.get<{ success: boolean; job: JobStatus }>(
            `/app-backfills/jobs/${jobId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          setJob(res.data.job);
          if (
            res.data.job.state === 'completed' ||
            res.data.job.state === 'failed'
          ) {
            clearInterval(interval);
          }
        } catch (err: any) {
          setError(err?.response?.data?.error || 'Failed to fetch job status');
        }
      });
    }, 1500);
    return () => clearInterval(interval);
  }, [jobId, withToken]);

  const addToBatch = useCallback((user: UserResult) => {
    setBatch((prev) => {
      if (prev.find((u) => u.id === user.id)) return prev;
      return [...prev, user];
    });
  }, []);

  const removeFromBatch = useCallback((userId: string) => {
    setBatch((prev) => prev.filter((u) => u.id !== userId));
  }, []);

  const addNextBatch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await withToken(async (token) =>
        api.get<{ success: boolean; users: UserResult[] }>(
          '/app-backfills/users',
          {
            params: { search: '', limit: 25 },
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
      );
      setBatch((prev) => {
        const existing = new Set(prev.map((u) => u.id));
        const added = (res.data.users || []).filter((u) => !existing.has(u.id));
        return [...prev, ...added];
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [withToken]);

  const runBackfill = useCallback(async () => {
    if (!selectedAppId || batch.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const res = await withToken(async (token) =>
        api.post<{
          success: boolean;
          jobId: string;
          app: { id: string; name: string };
          userCount: number;
          dryRun: boolean;
        }>(
          '/app-backfills/jobs',
          {
            appId: selectedAppId,
            userIds: batch.map((u) => u.id),
            dryRun,
            refreshViews,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      );
      setJobId(res.data.jobId);
      setJob(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to start backfill');
    } finally {
      setLoading(false);
    }
  }, [selectedAppId, batch, dryRun, refreshViews, withToken]);

  const progress: JobStatusProgress | null =
    typeof job?.progress === 'object' && job?.progress ? job.progress : null;
  const progressValue = progress?.completed ?? 0;
  const progressTotal = progress?.total ?? 0;

  if (hasAccess === false) {
    return (
      <div className='app-backfills'>
        <div className='access-denied'>Access denied.</div>
      </div>
    );
  }

  return (
    <div className='app-backfills'>
      <div className='header'>
        <h2>App Backfills</h2>
      </div>

      {error && <div className='msg error'>{error}</div>}

      <div className='section'>
        <label className='label'>App</label>
        <select
          className='app-select'
          value={selectedAppId}
          onChange={(e) => setSelectedAppId(e.target.value)}
        >
          {apps.map((app) => (
            <option key={app.id} value={app.id}>
              {app.name}
            </option>
          ))}
        </select>
      </div>

      <div className='section'>
        <label className='label'>Search users</label>
        <input
          className='search-input'
          type='text'
          placeholder='Name or user id'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {searchResults.length > 0 && (
          <div className='search-results'>
            {searchResults.map((user) => (
              <div key={user.id} className='search-row'>
                <span className='user-name'>
                  {user.name || 'Unnamed'} <small>({user.id})</small>
                </span>
                <button className='btn small' onClick={() => addToBatch(user)}>
                  Add
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className='section'>
        <div className='batch-header'>
          <label className='label'>
            Batch ({batch.length} user{batch.length === 1 ? '' : 's'})
          </label>
          <button className='btn link' onClick={addNextBatch}>
            Add next 25 secretary users
          </button>
        </div>
        {batch.length === 0 ? (
          <div className='empty-batch'>No users in the batch yet.</div>
        ) : (
          <div className='batch-chips'>
            {batch.map((user) => (
              <span key={user.id} className='chip'>
                {user.name || user.id}
                <button
                  className='chip-remove'
                  onClick={() => removeFromBatch(user.id)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className='section'>
        <label className='checkbox'>
          <input
            type='checkbox'
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
          />
          Dry run (do not create anything)
        </label>
        {apps.find((a) => a.id === selectedAppId)?.supportsRefresh && (
          <label className='checkbox'>
            <input
              type='checkbox'
              checked={refreshViews}
              onChange={(e) => setRefreshViews(e.target.checked)}
            />
            Refresh existing apps (replace view + routine instructions, keep
            data)
          </label>
        )}
      </div>

      <div className='section actions'>
        <button
          className='btn primary'
          disabled={batch.length === 0 || loading}
          onClick={runBackfill}
        >
          {loading ? 'Starting…' : 'Run backfill'}
        </button>
      </div>

      {job && (
        <div className='section progress-section'>
          <h3>
            {job.name} — {job.state}
          </h3>
          {typeof job.progress === 'object' &&
            job.progress &&
            progressTotal > 0 && (
              <>
                <div className='progress-bar'>
                  <div
                    className='progress-fill'
                    style={{
                      width: `${Math.round((progressValue / progressTotal) * 100)}%`,
                    }}
                  />
                </div>
                <div className='progress-meta'>
                  Processed {progressValue} of {progressTotal} users
                  {progress?.created !== undefined && (
                    <>
                      {' '}
                      • {progress.created} created, {progress.skipped ?? 0}{' '}
                      skipped, {progress.failed ?? 0} failed
                    </>
                  )}
                  {progress?.refreshed !== undefined && (
                    <>
                      {' '}
                      • {progress.refreshed} refreshed,{' '}
                      {progress.notInstalled ?? 0} not installed,{' '}
                      {progress.customised ?? 0} customised,{' '}
                      {progress.failed ?? 0} failed
                    </>
                  )}
                </div>
              </>
            )}
          {job.returnvalue && (
            <pre className='return-value'>
              {JSON.stringify(job.returnvalue, null, 2)}
            </pre>
          )}
          {job.failedReason && (
            <div className='msg error'>Failed: {job.failedReason}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AppBackfills;
